
/**
 * Formats a byte value into a human-readable string
 * @param bytes The number of bytes to format
 * @param decimals Number of decimal places to show (default: 2)
 * @returns Formatted string (e.g. "1.5 KB", "2.3 MB")
 */
export const formatBytes = (bytes: number, decimals: number = 2): string => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(decimals))} ${sizes[i]}`;
};

/**
 * Formats a number as a file size with the appropriate unit
 * @param size The size in bytes
 * @returns Formatted file size string
 */
export const formatFileSize = (size: number): string => {
  return formatBytes(size);
};

/**
 * Formats a currency value with the appropriate currency symbol
 * @param value The value to format
 * @param currency The currency code
 * @param locale The locale to use for formatting
 * @returns Formatted currency string
 */
export const formatCurrency = (
  value: string | number | null | undefined, 
  currency: string = 'EUR',
  locale: string = 'en-US'
): string => {
  if (value === null || value === undefined) {
    return 'N/A';
  }
  
  try {
    // Handle string values by converting to number
    const numValue = typeof value === 'string' ? parseFloat(value.replace(/[^\d.-]/g, '')) : value;
    
    if (isNaN(numValue)) {
      return `${value} ${currency}`;
    }
    
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency,
      maximumFractionDigits: 0
    }).format(numValue);
  } catch (error) {
    console.error('Error formatting currency:', error);
    return `${value} ${currency}`;
  }
};
